import type { CompareBy } from "@/run-detail/compare-by"
import {
  Card,
  CardContent,
  CardHeader,
  type CardOverflowMenuItem,
} from "@coval/ui/card"

import type { ComparisonPlotConfig } from "@/utils/comparisonPlot"

import { PLOT_REGISTRY } from "../constants"
import type { ComparisonMetricRecordsById, ComparisonRunRows } from "../types"
import { EditablePlotTitle } from "./EditablePlotTitle"

interface PlotCardProps {
  config: ComparisonPlotConfig
  runRows: ComparisonRunRows
  compareBy: CompareBy | null
  metricRecordsById: ComparisonMetricRecordsById
  onPointClick: (simulationOutputId: string) => void
  // Shared reports render plots read-only: no select, edit, or remove.
  readOnly: boolean
  selected: boolean
  // Remove is only offered while editing the layout. Reorder is handled by the
  // layout's drag wrapper, so the card itself owns no move controls.
  showRemove: boolean
  onSelect: () => void
  onTitleChange: (title: string) => void
  onRemove: () => void
}

// A comparison-plot block as it appears inline in the report layout. Clicking it
// selects the plot (opening its editor in the right pane); the chart renders via
// the registered plot type's visualization.
export function PlotCard({
  config,
  runRows,
  compareBy,
  metricRecordsById,
  onPointClick,
  readOnly,
  selected,
  showRemove,
  onSelect,
  onTitleChange,
  onRemove,
}: PlotCardProps) {
  const entry = PLOT_REGISTRY[config.type]

  const overflowMenu: CardOverflowMenuItem[] | undefined = showRemove
    ? [{ label: "Remove", onSelect: onRemove, destructive: true }]
    : undefined

  return (
    <Card
      interactive={!readOnly}
      onClick={readOnly ? undefined : onSelect}
      aria-label={
        readOnly ? undefined : `Select ${config.title || entry.label}`
      }
      aria-pressed={readOnly ? undefined : selected}
      className={selected ? "ring-1 ring-coval-border-active" : undefined}
    >
      <CardHeader overflowMenu={overflowMenu}>
        <EditablePlotTitle
          value={config.title ?? ""}
          placeholder={entry.label}
          editable={!readOnly}
          onChange={onTitleChange}
        />
      </CardHeader>
      <CardContent>
        <entry.Plot
          config={config}
          runRows={runRows}
          compareBy={compareBy}
          metricRecordsById={metricRecordsById}
          onPointClick={onPointClick}
          interactive={!readOnly}
        />
      </CardContent>
    </Card>
  )
}
